import { useState, useEffect } from 'react';
import Trophy from 'lucide-react/dist/esm/icons/trophy.js';
import Medal from 'lucide-react/dist/esm/icons/medal.js';
import Award from 'lucide-react/dist/esm/icons/award.js';
import TrendingUp from 'lucide-react/dist/esm/icons/trending-up.js';
import Crown from 'lucide-react/dist/esm/icons/crown.js';
import Star from 'lucide-react/dist/esm/icons/star.js';
import Users from 'lucide-react/dist/esm/icons/users.js';
import ArrowRight from 'lucide-react/dist/esm/icons/arrow-right.js';
import Building2 from 'lucide-react/dist/esm/icons/building-2.js';
import Settings from 'lucide-react/dist/esm/icons/settings.js';
import ChevronDown from 'lucide-react/dist/esm/icons/chevron-down.js';
import ChevronUp from 'lucide-react/dist/esm/icons/chevron-up.js';
import { Link } from 'react-router-dom';
import api, { apiRoutes } from '../api';
import { getConfig } from '../config/environment';
import InlineTranslationEdit from './InlineTranslationEdit';
import { useTranslations } from '../contexts/TranslationContext';
import { useLanguage } from '../contexts/LanguageContext';
import { formatCurrencyValue } from '../utils/formatCurrency';
import usePartnerData from '../hooks/usePartnerData';
import { useIsAdmin } from '../hooks/useIsAdmin';

export default function Leaderboard({
  editMode = false,
  period = 'currentDraw',
  limit = 10,
  showBadges = true,
  showPlacement: showPlacementProp = true,
  showSupporters: showSupportersProp = true,
  showTickets: showTicketsProp = true,
  showAmount: showAmountProp = true,
  displayStyle = 'list',
  sortBy = 'amount',
  mode = 'organizations',
  section = null,
  updateSection
}) {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  // How many org-mode entries are currently visible; grows on "Show more"
  const [visibleCount, setVisibleCount] = useState(limit);
  const { t, translations, updateTranslation } = useTranslations();
  const { language } = useLanguage();
  const { partners: orgPartners, fieldLabel, loading: orgLoading } = usePartnerData(mode === 'organizations' ? null : null, true, null, true, period, 'Leaderboard');
  const { isAdmin } = useIsAdmin();

  // Resolved display style: section config takes precedence, then prop, fallback to 'list'
  const activeDisplayStyle = section?.config?.displayStyle || displayStyle || 'list';

  // Resolved show placement & rankings: section config takes precedence, then showBadges prop, fallback true
  const showPlacement = (section?.config?.showPlacement ?? section?.config?.showBadges ?? showBadges) !== false;

  // Resolved show supporters, tickets & amount
  const showSupporters = (section?.config?.showSupporters ?? showSupportersProp) !== false;
  const showTickets = (section?.config?.showTickets ?? showTicketsProp) !== false;
  const showAmount = (section?.config?.showAmount ?? showAmountProp) !== false;

  // Resolved sort order: section config takes precedence, then prop, fallback to 'amount'
  const activeSortBy = section?.config?.sortBy || sortBy || 'amount';

  // Effective sort: ensure we do not sort by a field that is hidden
  const isSortAllowed = (k) => {
    if (k === 'amount') return showAmount;
    if (k === 'tickets') return showTickets;
    if (k === 'supporters') return showSupporters;
    return true;
  };
  const effectiveSortBy = isSortAllowed(activeSortBy)
    ? activeSortBy
    : (showAmount ? 'amount' : showTickets ? 'tickets' : showSupporters ? 'supporters' : 'name');

  // Sync visibleCount when limit prop changes (e.g. after settings save)
  useEffect(() => {
    setVisibleCount(limit);
  }, [limit]);

  // Settings dropdown state
  const [showSettings, setShowSettings] = useState(false);
  const [draftLimit, setDraftLimit] = useState(limit);
  const [draftPeriod, setDraftPeriod] = useState(period);
  const [draftShowPlacement, setDraftShowPlacement] = useState(showPlacement);
  const [draftShowSupporters, setDraftShowSupporters] = useState(showSupporters);
  const [draftShowTickets, setDraftShowTickets] = useState(showTickets);
  const [draftShowAmount, setDraftShowAmount] = useState(showAmount);
  const [draftDisplayStyle, setDraftDisplayStyle] = useState(activeDisplayStyle);
  const [draftSortBy, setDraftSortBy] = useState(activeSortBy);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const currentStyle = section?.config?.displayStyle || displayStyle || 'list';
    const currentPlacement = (section?.config?.showPlacement ?? section?.config?.showBadges ?? showBadges) !== false;
    const currentSupporters = (section?.config?.showSupporters ?? showSupportersProp) !== false;
    const currentTickets = (section?.config?.showTickets ?? showTicketsProp) !== false;
    const currentAmount = (section?.config?.showAmount ?? showAmountProp) !== false;
    const currentSort = section?.config?.sortBy || sortBy || 'amount';
    setDraftDisplayStyle(currentStyle);
    setDraftShowPlacement(currentPlacement);
    setDraftShowSupporters(currentSupporters);
    setDraftShowTickets(currentTickets);
    setDraftShowAmount(currentAmount);
    setDraftSortBy(currentSort);
  }, [section?.config?.displayStyle, section?.config?.showPlacement, section?.config?.showBadges, section?.config?.showSupporters, section?.config?.showTickets, section?.config?.showAmount, section?.config?.sortBy, displayStyle, showBadges, showSupportersProp, showTicketsProp, showAmountProp, sortBy]);

  useEffect(() => {
    const isDraftAllowed = (k) => {
      if (k === 'amount') return draftShowAmount;
      if (k === 'tickets') return draftShowTickets;
      if (k === 'supporters') return draftShowSupporters;
      return true;
    };
    if (!isDraftAllowed(draftSortBy)) {
      const fallback = draftShowAmount ? 'amount' : draftShowTickets ? 'tickets' : draftShowSupporters ? 'supporters' : 'name';
      setDraftSortBy(fallback);
    }
  }, [draftShowAmount, draftShowTickets, draftShowSupporters, draftSortBy]);

  const handleSaveSettings = async () => {
    if (!section || !updateSection) return;
    setSaving(true);
    try {
      const newConfig = {
        ...(section.config || {}),
        limit: Math.min(50, Math.max(1, Number(draftLimit) || 10)),
        period: draftPeriod,
        showBadges: draftShowPlacement,
        showPlacement: draftShowPlacement,
        showSupporters: draftShowSupporters,
        showTickets: draftShowTickets,
        showAmount: draftShowAmount,
        displayStyle: draftDisplayStyle,
        sortBy: draftSortBy,
      };
      await updateSection(section.id, { config: newConfig });
      setShowSettings(false);
    } finally {
      setSaving(false);
    }
  };

  useEffect(() => {
    if (mode === 'organizations') {
      const sorted = [...orgPartners].sort((a, b) => {
        switch (effectiveSortBy) {
          case 'tickets': {
            const diff = (b.total_tickets ?? 0) - (a.total_tickets ?? 0);
            return diff !== 0 ? diff : (b.total_amount ?? 0) - (a.total_amount ?? 0);
          }
          case 'supporters': {
            const diff = (b.supporter_count ?? 0) - (a.supporter_count ?? 0);
            return diff !== 0 ? diff : (b.total_amount ?? 0) - (a.total_amount ?? 0);
          }
          case 'name':
            return (a.organization_name || '').localeCompare(b.organization_name || '', language, { sensitivity: 'base' });
          case 'amount':
          default: {
            const diff = (b.total_amount ?? 0) - (a.total_amount ?? 0);
            return diff !== 0 ? diff : (b.total_tickets ?? 0) - (a.total_tickets ?? 0);
          }
        }
      });
      setEntries(sorted);
      setLoading(orgLoading);
    }
  }, [mode, orgPartners, orgLoading, effectiveSortBy, language]);

  useEffect(() => {
    if (mode !== 'organizations') {
      loadLeaderboardEntries();
    }
  }, [period, limit, mode]);

  const loadLeaderboardEntries = async () => {
    setLoading(true);
    try {
      const { tlfId } = getConfig();
      const res = await api.get(apiRoutes.SiteWinners, { params: { organizationId: tlfId } });
      const data = res.data?.payload || res.data || [];
      setEntries(Array.isArray(data) ? data.filter(e => e.is_visible !== false).slice(0, limit) : []);
    } catch (error) {
      console.error('Error loading leaderboard from API:', error);
      // Fallback to local data
      try {
        const leaderboardData = (await import('../data/leaderboard.json')).default;
        let data;
        if (period === 'yearly') {
          data = leaderboardData.yearlyLeaders || [];
        } else {
          data = leaderboardData.leaderboardEntries || [];
        }
        setEntries(data.filter(e => e.is_visible !== false).slice(0, limit));
      } catch { setEntries([]); }
    } finally {
      setLoading(false);
    }
  };

  const handleTranslationUpdate = async (key, value) => {
    await updateTranslation(key, value, language);
  };

  const getRankIcon = (rank) => {
    switch (rank) {
      case 1:
        return <Crown className="w-6 h-6 text-yellow-400" />;
      case 2:
        return <Medal className="w-6 h-6 text-gray-400" />;
      case 3:
        return <Award className="w-6 h-6 text-amber-600" />;
      default:
        return null;
    }
  };

  const getRankColor = (rank) => {
    switch (rank) {
      case 1:
        return 'bg-gradient-to-r from-yellow-400 to-yellow-600 text-white';
      case 2:
        return 'bg-gradient-to-r from-gray-300 to-gray-400 text-gray-900';
      case 3:
        return 'bg-gradient-to-r from-amber-500 to-amber-700 text-white';
      default:
        return 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300';
    }
  };

  const getBadgeColor = (badge) => {
    const badgeColors = {
      'Top Partner': 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200',
      'Top Supporter': 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200',
      'Champion': 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200',
      'Gold Tier': 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200',
    };
    return badgeColors[badge] || 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200';
  };

  const formatAmount = (amount) => formatCurrencyValue(amount, { language });
  const partnerLabel = fieldLabel || 'Partners';
  const withFieldName = (text) => text.replace(/{{fieldName}}/g, partnerLabel);
  const withFieldNameLower = (text) => text.replace(/{{fieldName}}/g, partnerLabel.toLowerCase());

  if (loading) {
    return (
      <div className="flex justify-center items-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  const renderSettingsDropdown = () => {
    if (!editMode && !isAdmin) return null;
    return (
      <div className="absolute top-0 left-1/2 -translate-x-1/2 z-30 flex flex-col items-center">
        {showSettings ? (
          <div className="rounded-b-xl bg-black/80 backdrop-blur-md border border-t-0 border-white/20 px-4 pt-2 pb-3 flex flex-col gap-2 min-w-[280px] shadow-xl">
            <div className="flex items-center justify-between gap-3">
              <span className="text-[10px] font-semibold text-white/60 uppercase tracking-wider flex items-center gap-1">
                <Settings className="w-3 h-3" /> {t('admin.leaderboardSettings.title', 'Leaderboard Settings')}
              </span>
              <button
                type="button"
                onClick={() => setShowSettings(false)}
                className="p-0.5 rounded hover:bg-white/15 text-white/70 hover:text-white transition-colors"
              >
                <ChevronUp className="w-4 h-4" />
              </button>
            </div>
            <div className="space-y-2 pt-1 border-t border-white/10">
              <div>
                <label className="block text-[10px] text-white/60 mb-1">{t('admin.leaderboardSettings.maxEntries', 'Max entries (1–50)')}</label>
                <input
                  type="number"
                  min={1}
                  max={50}
                  value={draftLimit}
                  onChange={e => setDraftLimit(e.target.value)}
                  className="w-full rounded-lg bg-white/10 border border-white/20 text-white text-sm px-3 py-1.5 focus:outline-none focus:border-white/40"
                />
              </div>
              <div>
                <label className="block text-[10px] text-white/60 mb-1">{t('partners.period', 'Period')}</label>
                <select
                  value={draftPeriod}
                  onChange={e => setDraftPeriod(e.target.value)}
                  className="w-full rounded-lg bg-white/10 border border-white/20 text-white text-sm px-3 py-1.5 focus:outline-none focus:border-white/40"
                >
                  <option value="currentDraw" className="bg-gray-900">{t('partners.currentDraw', 'Current Draw')}</option>
                  <option value="monthly" className="bg-gray-900">{t('partners.monthly', 'Monthly')}</option>
                  <option value="yearly" className="bg-gray-900">{t('partners.yearly', 'Yearly')}</option>
                  <option value="alltime" className="bg-gray-900">{t('partners.allTime', 'All Time')}</option>
                </select>
              </div>
              <div>
                <label className="block text-[10px] text-white/60 mb-1">{t('admin.leaderboardSettings.displayStyle', 'Display Style')}</label>
                <select
                  value={draftDisplayStyle}
                  onChange={e => {
                    const newStyle = e.target.value;
                    setDraftDisplayStyle(newStyle);
                    if (newStyle === 'cards') {
                      setDraftShowPlacement(false);
                    } else if (newStyle === 'podium') {
                      setDraftShowPlacement(true);
                    }
                  }}
                  className="w-full rounded-lg bg-white/10 border border-white/20 text-white text-sm px-3 py-1.5 focus:outline-none focus:border-white/40"
                >
                  <option value="list" className="bg-gray-900">{t('admin.leaderboardSettings.styleList', 'Stacked List View')}</option>
                  <option value="podium" className="bg-gray-900">{t('admin.leaderboardSettings.stylePodium', 'Podium View')}</option>
                  <option value="table" className="bg-gray-900">{t('admin.leaderboardSettings.styleTable', 'Table View')}</option>
                  <option value="cards" className="bg-gray-900">{t('admin.leaderboardSettings.styleCards', 'Grid Cards View')}</option>
                </select>
              </div>
              <div>
                <label className="block text-[10px] text-white/60 mb-1">{t('admin.leaderboardSettings.sortBy', 'Sort By')}</label>
                <select
                  value={draftSortBy}
                  onChange={e => setDraftSortBy(e.target.value)}
                  className="w-full rounded-lg bg-white/10 border border-white/20 text-white text-sm px-3 py-1.5 focus:outline-none focus:border-white/40"
                >
                  {draftShowAmount && <option value="amount" className="bg-gray-900">{t('admin.leaderboardSettings.sortAmount', 'Total Raised (High to Low)')}</option>}
                  {draftShowTickets && <option value="tickets" className="bg-gray-900">{t('admin.leaderboardSettings.sortTickets', 'Tickets Sold (High to Low)')}</option>}
                  {draftShowSupporters && <option value="supporters" className="bg-gray-900">{t('admin.leaderboardSettings.sortSupporters', 'Supporters (High to Low)')}</option>}
                  <option value="name" className="bg-gray-900">{t('admin.leaderboardSettings.sortName', 'Alphabetical (A to Z)')}</option>
                </select>
              </div>
              <label className="flex items-center gap-2 cursor-pointer mt-0.5">
                <input
                  type="checkbox"
                  checked={draftShowPlacement}
                  onChange={e => setDraftShowPlacement(e.target.checked)}
                  className="rounded border-white/20 bg-white/10 text-indigo-500 focus:ring-indigo-500"
                />
                <span className="text-[10px] text-white/70">{t('partners.showPlacement', 'Show placement & rankings')}</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer mt-0.5">
                <input
                  type="checkbox"
                  checked={draftShowSupporters}
                  onChange={e => setDraftShowSupporters(e.target.checked)}
                  className="rounded border-white/20 bg-white/10 text-indigo-500 focus:ring-indigo-500"
                />
                <span className="text-[10px] text-white/70">{t('partners.showSupporters', 'Show supporters')}</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer mt-0.5">
                <input
                  type="checkbox"
                  checked={draftShowTickets}
                  onChange={e => setDraftShowTickets(e.target.checked)}
                  className="rounded border-white/20 bg-white/10 text-indigo-500 focus:ring-indigo-500"
                />
                <span className="text-[10px] text-white/70">{t('partners.showTickets', 'Show tickets sold')}</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer mt-0.5">
                <input
                  type="checkbox"
                  checked={draftShowAmount}
                  onChange={e => setDraftShowAmount(e.target.checked)}
                  className="rounded border-white/20 bg-white/10 text-indigo-500 focus:ring-indigo-500"
                />
                <span className="text-[10px] text-white/70">{t('partners.showAmount', 'Show amount raised')}</span>
              </label>
            </div>
            <button
              type="button"
              onClick={handleSaveSettings}
              disabled={saving}
              className="mt-1 w-full rounded-lg py-1.5 text-xs font-semibold text-white transition-opacity disabled:opacity-50"
              style={{ background: 'var(--charity-primary, #e75480)' }}
            >
              {saving ? t('admin.common.saving', 'Saving…') : t('admin.leaderboardSettings.saveSettings', 'Save Settings')}
            </button>
          </div>
        ) : (
          <button
            type="button"
            onClick={() => {
              setDraftLimit(limit);
              setDraftPeriod(period);
              setDraftShowPlacement(showPlacement);
              setDraftShowSupporters(showSupporters);
              setDraftShowTickets(showTickets);
              setDraftShowAmount(showAmount);
              setDraftDisplayStyle(activeDisplayStyle);
              setDraftSortBy(activeSortBy);
              setShowSettings(true);
            }}
            className="rounded-b-lg bg-black/60 backdrop-blur-md border border-t-0 border-white/20 px-3 py-1 text-[10px] font-semibold text-white/60 hover:text-white hover:bg-black/80 transition-colors flex items-center gap-1"
          >
            <Settings className="w-3 h-3" /> <ChevronDown className="w-3 h-3" />
          </button>
        )}
      </div>
    );
  };

  const renderOrgRow = (org, index) => (
    <Link
      to={`/partner/${org.id}`}
      key={org.id}
      className={`block cursor-pointer rounded-xl p-3 sm:p-3.5 transition-all duration-200 hover:bg-white/10 ${
        showPlacement && index === 0
          ? 'bg-amber-500/10 dark:bg-amber-500/10 border-l-4 border-amber-400'
          : showPlacement && index === 1
          ? 'bg-slate-500/10 dark:bg-slate-400/10 border-l-4 border-slate-300'
          : showPlacement && index === 2
          ? 'bg-amber-700/10 dark:bg-amber-600/10 border-l-4 border-amber-600'
          : 'bg-gray-50/80 dark:bg-white/5 border-l-4 border-transparent'
      }`}
    >
      <div className="flex items-center gap-2.5 sm:gap-4 min-w-0">
        {showPlacement ? (
          <div
            className={`w-9 h-9 sm:w-12 sm:h-12 rounded-full ${
              index === 0
                ? 'bg-gradient-to-r from-yellow-400 to-yellow-600 text-white shadow-md'
                : index === 1
                ? 'bg-gradient-to-r from-gray-300 to-gray-400 text-gray-900'
                : index === 2
                ? 'bg-gradient-to-r from-amber-500 to-amber-700 text-white'
                : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300'
            } flex items-center justify-center font-bold text-sm sm:text-lg flex-shrink-0`}
          >
            {index < 3 ? getRankIcon(index + 1) : `#${index + 1}`}
          </div>
        ) : (
          <div className="w-9 h-9 sm:w-12 sm:h-12 rounded-full bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400 flex items-center justify-center flex-shrink-0">
            <Building2 className="w-4 h-4 sm:w-5 sm:h-5 opacity-70" />
          </div>
        )}

        {org.organization_logo_url && (
          <img
            src={org.organization_logo_url}
            alt={org.organization_name}
            className="w-8 h-8 sm:w-10 sm:h-10 rounded-full object-cover border-2 border-gray-200 dark:border-gray-600 bg-white flex-shrink-0"
          />
        )}

        <div className="flex-1 min-w-0">
          <h3 className="font-bold text-sm sm:text-base text-gray-900 dark:text-white truncate leading-snug">
            {org.organization_name}
          </h3>
          <div className="flex flex-wrap items-center gap-x-2.5 sm:gap-x-3 gap-y-0.5 text-[11px] sm:text-sm text-gray-600 dark:text-gray-400 mt-0.5 sm:mt-1">
            {showSupporters && (
              <span className="flex items-center gap-1">
                <Users className="w-3 h-3 sm:w-4 sm:h-4 shrink-0 opacity-70" />
                {org.supporter_count.toLocaleString()} {t('leaderboard.supporters')}
              </span>
            )}
            {showTickets && (
              <span className="flex items-center gap-1">
                <TrendingUp className="w-3 h-3 sm:w-4 sm:h-4 shrink-0 opacity-70" />
                {org.total_tickets.toLocaleString()} {t('leaderboard.tickets')}
              </span>
            )}
            {showAmount && (
              <span className="font-extrabold text-green-600 dark:text-green-400">
                {formatAmount(org.total_amount)}
              </span>
            )}
          </div>
        </div>

        {showPlacement && index === 0 && (
          <div className="hidden sm:flex items-center justify-center flex-shrink-0">
            <div className="relative">
              <Trophy className="w-10 h-10 text-yellow-500 animate-pulse" />
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="w-12 h-12 bg-yellow-400 rounded-full opacity-20 animate-ping"></div>
              </div>
            </div>
          </div>
        )}
      </div>
    </Link>
  );

  const renderOrgPodium = (items) => {
    const topThreeItems = items.slice(0, 3);
    const restItems = items.slice(3);
    const statCols = (showSupporters ? 1 : 0) + (showTickets ? 1 : 0) + (showAmount ? 1 : 0);
    const gridColsClass = statCols === 3 ? 'grid-cols-3' : statCols === 2 ? 'grid-cols-2' : 'grid-cols-1';

    return (
      <div className="space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3.5 sm:gap-4 items-end">
          {topThreeItems.map((org, idx) => {
            const rank = idx + 1;
            const theme = rank === 1
              ? { header: 'from-yellow-400 via-amber-400 to-yellow-500', ring: 'ring-yellow-400/60', order: 'md:order-2 md:-translate-y-2 md:scale-[1.03]' }
              : rank === 2
              ? { header: 'from-slate-300 via-gray-300 to-slate-400', ring: 'ring-slate-300/60', order: 'md:order-1' }
              : { header: 'from-amber-500 via-orange-500 to-amber-600', ring: 'ring-amber-500/60', order: 'md:order-3' };
            return (
              <Link
                to={`/partner/${org.id}`}
                key={org.id}
                className={`group relative block cursor-pointer bg-white dark:bg-white/5 rounded-2xl shadow-md hover:shadow-xl ring-1 ${theme.ring} overflow-hidden transition-all duration-300 hover:-translate-y-1 ${theme.order}`}
              >
                <div className={`h-14 bg-gradient-to-r ${theme.header}`} />
                {showPlacement && (
                  <div className="absolute top-2.5 right-2.5 flex items-center gap-1 px-2 py-0.5 rounded-full bg-black/30 backdrop-blur-sm text-white text-xs font-bold">
                    {getRankIcon(rank)} #{rank}
                  </div>
                )}
                <div className="px-4 -mt-8 text-center">
                  <div className={`inline-flex rounded-full p-1 bg-white dark:bg-gray-800 ring-2 ${theme.ring} shadow`}>
                    {org.organization_logo_url ? (
                      <img src={org.organization_logo_url} alt={org.organization_name} className="w-14 h-14 rounded-full object-cover bg-white" />
                    ) : (
                      <div className="w-14 h-14 rounded-full bg-gradient-to-br from-blue-500 to-purple-500 flex items-center justify-center text-white font-bold text-lg">
                        {org.organization_name?.charAt(0)}
                      </div>
                    )}
                  </div>
                </div>
                <div className="px-3 pt-2 pb-3.5 text-center">
                  <h3 className="font-bold text-sm text-gray-900 dark:text-white truncate">
                    {org.organization_name}
                  </h3>
                  {statCols > 0 && (
                    <div className={`grid ${gridColsClass} gap-1 pt-2.5 mt-2 border-t border-gray-100 dark:border-white/10 text-center`}>
                      {showSupporters && (
                        <div>
                          <p className="text-xs font-semibold text-gray-900 dark:text-white">{org.supporter_count.toLocaleString()}</p>
                          <p className="text-[9px] uppercase tracking-wider text-gray-500 dark:text-gray-400">{t('leaderboard.supporters')}</p>
                        </div>
                      )}
                      {showTickets && (
                        <div>
                          <p className="text-xs font-semibold text-gray-900 dark:text-white">{org.total_tickets.toLocaleString()}</p>
                          <p className="text-[9px] uppercase tracking-wider text-gray-500 dark:text-gray-400">{t('leaderboard.tickets')}</p>
                        </div>
                      )}
                      {showAmount && (
                        <div>
                          <p className="text-xs font-bold text-green-600 dark:text-green-400">{formatAmount(org.total_amount)}</p>
                          <p className="text-[9px] uppercase tracking-wider text-gray-500 dark:text-gray-400">{t('partners.raised', 'Raised')}</p>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </Link>
            );
          })}
        </div>
        {restItems.length > 0 && (
          <div className="space-y-2 mt-4">
            {restItems.map((org, idx) => renderOrgRow(org, idx + 3))}
          </div>
        )}
      </div>
    );
  };

  const renderOrgTable = (items) => (
    <div className="overflow-x-auto">
      <table className="w-full text-left text-sm">
        <thead>
          <tr className="border-b border-gray-200 dark:border-white/10 text-xs uppercase tracking-wider text-gray-500 dark:text-gray-400">
            {showPlacement && <th className="py-2.5 px-3 w-12 font-semibold">#</th>}
            <th className="py-2.5 px-3 font-semibold">{partnerLabel}</th>
            {showSupporters && <th className="py-2.5 px-3 text-right font-semibold hidden sm:table-cell">{t('leaderboard.supporters')}</th>}
            {showTickets && <th className="py-2.5 px-3 text-right font-semibold">{t('leaderboard.tickets')}</th>}
            {showAmount && <th className="py-2.5 px-3 text-right font-semibold">{t('partners.raised', 'Raised')}</th>}
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100 dark:divide-white/5">
          {items.map((org, index) => (
            <tr key={org.id} className="hover:bg-gray-50/70 dark:hover:bg-white/5 transition-colors">
              {showPlacement && (
                <td className="py-2.5 px-3 font-bold text-gray-700 dark:text-gray-300">
                  <span className="inline-flex items-center gap-1">
                    {index < 3 ? getRankIcon(index + 1) : null}
                    <span>#{index + 1}</span>
                  </span>
                </td>
              )}
              <td className="py-2.5 px-3">
                <Link to={`/partner/${org.id}`} className="flex items-center gap-2.5 group">
                  {org.organization_logo_url ? (
                    <img src={org.organization_logo_url} alt={org.organization_name} className="w-7 h-7 sm:w-8 sm:h-8 rounded-full object-cover border border-gray-200 dark:border-gray-600 bg-white shrink-0" />
                  ) : (
                    <div className="w-7 h-7 sm:w-8 sm:h-8 rounded-full bg-gradient-to-br from-blue-500 to-purple-500 flex items-center justify-center text-white font-bold text-xs shrink-0">
                      {org.organization_name?.charAt(0)}
                    </div>
                  )}
                  <span className="font-semibold text-gray-900 dark:text-white group-hover:text-blue-600 dark:group-hover:text-blue-400 truncate max-w-[160px] sm:max-w-none">
                    {org.organization_name}
                  </span>
                </Link>
              </td>
              {showSupporters && (
                <td className="py-2.5 px-3 text-right text-gray-600 dark:text-gray-300 hidden sm:table-cell">
                  {org.supporter_count.toLocaleString()}
                </td>
              )}
              {showTickets && (
                <td className="py-2.5 px-3 text-right text-gray-600 dark:text-gray-300">
                  {org.total_tickets.toLocaleString()}
                </td>
              )}
              {showAmount && (
                <td className="py-2.5 px-3 text-right font-bold text-green-600 dark:text-green-400">
                  {formatAmount(org.total_amount)}
                </td>
              )}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );

  const renderOrgCards = (items) => {
    const statCols = (showSupporters ? 1 : 0) + (showTickets ? 1 : 0) + (showAmount ? 1 : 0);
    const gridColsClass = statCols === 3 ? 'grid-cols-3' : statCols === 2 ? 'grid-cols-2' : 'grid-cols-1';

    return (
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
        {items.map((org, index) => (
          <Link
            to={`/partner/${org.id}`}
            key={org.id}
            className="block bg-gray-50/80 dark:bg-white/5 hover:bg-white dark:hover:bg-white/10 rounded-2xl p-4 border border-gray-100 dark:border-white/10 shadow-sm hover:shadow-md transition-all duration-200"
          >
            <div className="flex items-center gap-3 mb-3">
              {showPlacement && (
                <div className={`w-8 h-8 rounded-full ${getRankColor(index + 1)} flex items-center justify-center font-bold text-xs shrink-0`}>
                  {index < 3 ? getRankIcon(index + 1) : `#${index + 1}`}
                </div>
              )}
              {org.organization_logo_url && (
                <img src={org.organization_logo_url} alt={org.organization_name} className="w-9 h-9 rounded-full object-cover border border-gray-200 dark:border-gray-600 bg-white shrink-0" />
              )}
              <div className="min-w-0 flex-1">
                <h3 className="font-bold text-sm text-gray-900 dark:text-white truncate">
                  {org.organization_name}
                </h3>
              </div>
            </div>
            {statCols > 0 && (
              <div className={`grid ${gridColsClass} gap-1 pt-2 border-t border-gray-200/60 dark:border-white/10 text-center`}>
                {showSupporters && (
                  <div>
                    <p className="text-xs font-semibold text-gray-900 dark:text-white">{org.supporter_count.toLocaleString()}</p>
                    <p className="text-[9px] uppercase tracking-wider text-gray-500 dark:text-gray-400">{t('leaderboard.supporters')}</p>
                  </div>
                )}
                {showTickets && (
                  <div>
                    <p className="text-xs font-semibold text-gray-900 dark:text-white">{org.total_tickets.toLocaleString()}</p>
                    <p className="text-[9px] uppercase tracking-wider text-gray-500 dark:text-gray-400">{t('leaderboard.tickets')}</p>
                  </div>
                )}
                {showAmount && (
                  <div>
                    <p className="text-xs font-bold text-green-600 dark:text-green-400">{formatAmount(org.total_amount)}</p>
                    <p className="text-[9px] uppercase tracking-wider text-gray-500 dark:text-gray-400">{t('partners.raised', 'Raised')}</p>
                  </div>
                )}
              </div>
            )}
          </Link>
        ))}
      </div>
    );
  };

  if (mode === 'organizations') {
    if (!entries.length && !editMode) return null;
    const visibleEntries = entries.slice(0, visibleCount);
    const hasMore = entries.length > visibleCount;
    return (
      <div className="relative bg-white dark:bg-white/[0.03] dark:backdrop-blur-sm rounded-2xl sm:rounded-3xl border border-gray-100 dark:border-white/10 shadow-xl overflow-hidden">
        {renderSettingsDropdown()}
        <div className="p-4 sm:p-6 text-white" style={{ background: 'linear-gradient(135deg, var(--charity-gradient-start), var(--charity-gradient-end))' }}>
          <div className="flex items-center gap-2.5 sm:gap-3 mb-1.5 sm:mb-2">
            <Trophy className="w-6 h-6 sm:w-8 sm:h-8 shrink-0" />
            <h2 className="text-xl sm:text-3xl font-bold tracking-tight">
              <InlineTranslationEdit
                translationKey="leaderboard.title"
                value={t('leaderboard.title')}
                fallback="Top {{fieldName}}"
                displayValue={withFieldName(t('leaderboard.title'))}
                onUpdate={handleTranslationUpdate}
                editMode={editMode}
                className="text-xl sm:text-3xl font-bold text-white"
              />
            </h2>
          </div>
          <p className="text-xs sm:text-base text-white/80">
            <InlineTranslationEdit
              translationKey="leaderboard.subtitle"
              value={t('leaderboard.subtitle')}
              fallback="Thank you to our amazing {{fieldName}} who make this possible!"
              displayValue={withFieldNameLower(t('leaderboard.subtitle'))}
              onUpdate={handleTranslationUpdate}
              editMode={editMode}
              className="text-xs sm:text-base text-white/80"
            />
          </p>
        </div>

        <div className="p-3.5 sm:p-6">
          {entries.length === 0 ? (
            <div className="text-center py-8 text-gray-500 dark:text-gray-400">
              <TrendingUp className="w-12 h-12 mx-auto mb-3 opacity-50" />
              <p>{t('leaderboard.noOrgs')}</p>
            </div>
          ) : activeDisplayStyle === 'podium' ? (
            renderOrgPodium(visibleEntries)
          ) : activeDisplayStyle === 'table' ? (
            renderOrgTable(visibleEntries)
          ) : activeDisplayStyle === 'cards' ? (
            renderOrgCards(visibleEntries)
          ) : (
            <div className="space-y-2">
              {visibleEntries.map((org, index) => renderOrgRow(org, index))}
            </div>
          )}

          <div className="mt-6 pt-6 border-t border-gray-200 dark:border-gray-700 space-y-4">
            {hasMore && (
              <button
                onClick={() => setVisibleCount(c => c + limit)}
                className="w-full py-2 text-sm font-semibold text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white transition-colors"
              >
                {t('leaderboard.showMore', 'Show more')} ({entries.length - visibleCount} {t('leaderboard.remaining', 'remaining')})
              </button>
            )}
            {visibleCount > limit && (
              <button
                onClick={() => setVisibleCount(limit)}
                className="w-full py-1 text-xs text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
              >
                {t('leaderboard.showLess', 'Show less')}
              </button>
            )}
            {entries.length > 0 && (
            <Link
              to="/all-partners"
              className="flex items-center justify-center gap-2 py-3 px-6 text-white rounded-lg transition-all duration-200 font-semibold shadow-md hover:shadow-lg hover:opacity-90"
              style={{ background: 'linear-gradient(135deg, var(--charity-gradient-start), var(--charity-gradient-end))' }}
            >
              {withFieldName(t('leaderboard.viewAllPartners', 'View All {{fieldName}}'))}
              <ArrowRight className="w-5 h-5" />
            </Link>
            )}
            <p className="text-center text-sm text-gray-600 dark:text-gray-400">
              <InlineTranslationEdit
                translationKey="leaderboard.footer"
                value={t('leaderboard.footer')}
                fallback="Rankings update in real time. Keep playing to climb the leaderboard!"
                onUpdate={handleTranslationUpdate}
                editMode={editMode}
                className="text-sm text-gray-600 dark:text-gray-400"
              />
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="relative bg-white dark:bg-white/[0.03] dark:backdrop-blur-sm rounded-2xl sm:rounded-3xl border border-gray-100 dark:border-white/10 shadow-xl overflow-hidden">
      {renderSettingsDropdown()}
      <div className="p-4 sm:p-6 text-white" style={{ background: 'linear-gradient(135deg, var(--charity-gradient-start), var(--charity-gradient-end))' }}>
        <div className="flex items-center gap-2.5 sm:gap-3 mb-1.5 sm:mb-2">
          <Trophy className="w-6 h-6 sm:w-8 sm:h-8 shrink-0" />
          <h2 className="text-xl sm:text-3xl font-bold tracking-tight">
            <InlineTranslationEdit
              translationKey="leaderboard.title"
              value={t('leaderboard.title')}
              fallback="Top {{fieldName}}"
              displayValue={withFieldName(t('leaderboard.title'))}
              onUpdate={handleTranslationUpdate}
              editMode={editMode}
              className="text-xl sm:text-3xl font-bold text-white"
            />
          </h2>
        </div>
        <p className="text-xs sm:text-base text-blue-100">
          <InlineTranslationEdit
            translationKey="leaderboard.subtitle"
            value={t('leaderboard.subtitle')}
            fallback="Thank you to our amazing {{fieldName}} who make this possible!"
            displayValue={withFieldNameLower(t('leaderboard.subtitle'))}
            onUpdate={handleTranslationUpdate}
            editMode={editMode}
            className="text-xs sm:text-base text-blue-100"
          />
        </p>
      </div>

      <div className="p-3.5 sm:p-6">
        {entries.length === 0 ? (
          <div className="text-center py-8 text-gray-500 dark:text-gray-400">
            <TrendingUp className="w-12 h-12 mx-auto mb-3 opacity-50" />
            <p>{t('leaderboard.noEntries')}</p>
          </div>
        ) : (
          <div className="space-y-2">
            {entries.map((entry, index) => (
              <div
                key={entry.id}
                className={`rounded-xl p-3 sm:p-3.5 transition-all duration-200 hover:bg-white/10 ${
                  entry.rank === 1
                    ? 'bg-amber-500/10 dark:bg-amber-500/10 border-l-4 border-amber-400'
                    : entry.rank === 2
                    ? 'bg-slate-500/10 dark:bg-slate-400/10 border-l-4 border-slate-300'
                    : entry.rank === 3
                    ? 'bg-amber-700/10 dark:bg-amber-600/10 border-l-4 border-amber-600'
                    : 'bg-gray-50/80 dark:bg-white/5 border-l-4 border-transparent'
                }`}
              >
                <div className="flex items-center gap-2.5 sm:gap-4 min-w-0">
                  <div
                    className={`w-9 h-9 sm:w-12 sm:h-12 rounded-full ${getRankColor(
                      entry.rank
                    )} flex items-center justify-center font-bold text-sm sm:text-lg flex-shrink-0`}
                  >
                    {entry.rank <= 3 ? getRankIcon(entry.rank) : `#${entry.rank}`}
                  </div>

                  {entry.avatar_url && (
                    <img
                      src={entry.avatar_url}
                      alt={entry.display_name}
                      className="w-8 h-8 sm:w-10 sm:h-10 rounded-full object-cover border-2 border-gray-200 dark:border-gray-600 flex-shrink-0"
                    />
                  )}

                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <h3 className="font-bold text-sm sm:text-base text-gray-900 dark:text-white truncate">
                        {entry.display_name}
                      </h3>
                      {showBadges && entry.badge && (
                        <span
                          className={`inline-flex items-center gap-1 px-2 py-0.5 sm:py-1 rounded-full text-[10px] sm:text-xs font-semibold ${getBadgeColor(
                            entry.badge
                          )}`}
                        >
                          <Star className="w-3 h-3" />
                          {entry.badge}
                        </span>
                      )}
                    </div>
                    <div className="flex items-center gap-3 text-xs sm:text-sm text-gray-600 dark:text-gray-400 mt-0.5 sm:mt-1">
                      <span className="flex items-center gap-1">
                        <TrendingUp className="w-3.5 h-3.5 sm:w-4 sm:h-4 shrink-0 opacity-70" />
                        {entry.tickets_purchased.toLocaleString()} {t('leaderboard.tickets')}
                      </span>
                      <span className="font-extrabold text-green-600 dark:text-green-400">
                        {formatAmount(entry.amount_spent)}
                      </span>
                    </div>
                  </div>

                  {entry.rank === 1 && (
                    <div className="hidden sm:flex items-center justify-center flex-shrink-0">
                      <div className="relative">
                        <Trophy className="w-10 h-10 text-yellow-500 animate-pulse" />
                        <div className="absolute inset-0 flex items-center justify-center">
                          <div className="w-12 h-12 bg-yellow-400 rounded-full opacity-20 animate-ping"></div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}

        <div className="mt-6 pt-6 border-t border-gray-200 dark:border-gray-700">
          <p className="text-center text-sm text-gray-600 dark:text-gray-400">
            <InlineTranslationEdit
              translationKey="leaderboard.footer"
              value={t('leaderboard.footer')}
              fallback="Rankings update in real time. Keep playing to climb the leaderboard!"
              onUpdate={handleTranslationUpdate}
              editMode={editMode}
              className="text-sm text-gray-600 dark:text-gray-400"
            />
          </p>
        </div>
      </div>
    </div>
  );
}
